import React, {CSSProperties} from "react";
import {useTranslation} from "react-i18next";
import {StartlistConfiguration} from "../models/Models";
import {Translation} from "../helper/WinfireLiveConstHelper";

/** Lanes 1–16 are listed on the left, 17–32 on the right. */
const LANES_PER_COLUMN = 16;
const COLUMN_COUNT = 2;

/** Sized to the screen height, so the heading row and all 16 lanes fit below the header. */
const CELL_FONT_SIZE = 'clamp(1rem, 3.3vh, 3rem)';

const containerStyle: CSSProperties = {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: '1vh 2vw 2vh'
};

const columnsStyle: CSSProperties = {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    gap: '2vw'
};

/** One row per lane plus the heading row, all sharing the available height. */
const columnStyle: CSSProperties = {
    flex: '1 1 0',
    minWidth: 0,
    display: 'grid',
    gridTemplateRows: `repeat(${LANES_PER_COLUMN + 1}, 1fr)`
};

const rowStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    minHeight: 0,
    fontSize: CELL_FONT_SIZE
};

const laneCellStyle: CSSProperties = {
    width: '22%',
    textAlign: 'end',
    paddingRight: '1.5vw',
    fontWeight: 700
};

const nameCellStyle: CSSProperties = {
    flex: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
};

interface StartlistViewProps {
    /** Subtitle of the configuration, usually the name of the round. */
    subTitle: string;
    startlistConfig: StartlistConfiguration | undefined | null;
}

/**
 * Lists lane number and "Lastname Firstname" of the participants of one round in two columns of 16
 * lanes. Every lane is shown, also the ones without participant, so the list mirrors the range.
 */
const StartlistView: React.FC<StartlistViewProps> = ({subTitle, startlistConfig}) => {
    const {t} = useTranslation();
    const namesByLane = new Map(
        (startlistConfig?.entries ?? []).map(entry => [entry.laneNr ?? 0, entry.participantName ?? ''])
    );

    return <div style={containerStyle}>
        {subTitle && (
            <h1 className="text-center fw-bold mb-2" style={{fontSize: 'clamp(1.5rem, 5vh, 4rem)'}}>
                {subTitle}
            </h1>
        )}
        <div style={columnsStyle}>
            {Array.from({length: COLUMN_COUNT}, (_, column) => (
                <div key={column} style={columnStyle}>
                    <div style={{...rowStyle, borderBottom: '2px solid #333'}}>
                        <span style={laneCellStyle}>{t(Translation.LANE)}</span>
                        <span style={{...nameCellStyle, fontWeight: 700}}>{t(Translation.NAME)}</span>
                    </div>
                    {Array.from({length: LANES_PER_COLUMN}, (__, row) => {
                        const laneNr = column * LANES_PER_COLUMN + row + 1;
                        return <div
                            key={laneNr}
                            style={{...rowStyle, backgroundColor: row % 2 === 0 ? '#ffffff' : '#e9ecef'}}
                        >
                            <span style={laneCellStyle}>{laneNr}</span>
                            <span style={nameCellStyle}>{namesByLane.get(laneNr) ?? ''}</span>
                        </div>;
                    })}
                </div>
            ))}
        </div>
    </div>;
};

export default StartlistView;
